import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Divider,
  IconButton,
  alpha,
  useTheme,
  Chip,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonIcon from "@mui/icons-material/Person";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AssignmentIcon from "@mui/icons-material/Assignment";

const ConflictModal = ({ open, onClose, conflictData, onAssignAnyway }) => {
  const theme = useTheme();

  const renderDetail = (label, value, icon) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        bgcolor: "#F8FAFC",
        borderRadius: "6px",
        border: "1px solid #E2E8F0",
      }}
    >
      {icon && <Box sx={{ color: "#64748B", display: "flex" }}>{icon}</Box>}
      <Box>
        <Typography variant="caption" color="#6B7280" fontWeight={500}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={600} color="#1A1A2E">
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.error.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WarningAmberIcon sx={{ color: theme.palette.error.main, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#1A1A2E">
                Schedule Conflict Detected
              </Typography>
              <Typography variant="caption" color="#6B7280">
                {conflictData?.length || 0} conflict(s) found
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2 }}>
        {conflictData?.length > 0 ? (
          <Stack spacing={2.5}>
            {conflictData.map((conflict, index) => (
              <Card
                key={index}
                elevation={0}
                sx={{
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: alpha(theme.palette.error.main, 0.15),
                  bgcolor: alpha(theme.palette.error.main, 0.02),
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    bgcolor: alpha(theme.palette.error.main, 0.06),
                    borderBottom: "1px solid",
                    borderColor: alpha(theme.palette.error.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.error.main, 0.12),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 18, color: theme.palette.error.main }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={700} color="#1A1A2E">
                    {conflict.name || "Unknown Candidate"}
                  </Typography>
                  <Chip
                    label={`Conflict #${index + 1}`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.55rem",
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.error.main, 0.12),
                      color: theme.palette.error.main,
                    }}
                  />
                </Box>

                <CardContent sx={{ p: 2.5 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      {renderDetail(
                        "Client",
                        conflict.conflict_info?.client,
                        <PersonIcon sx={{ fontSize: 16 }} />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderDetail(
                        "Visit",
                        conflict.conflict_info?.visit,
                        <AssignmentIcon sx={{ fontSize: 16 }} />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderDetail(
                        "Run",
                        conflict.conflict_info?.run,
                        <LocationOnIcon sx={{ fontSize: 16 }} />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderDetail(
                        "Day",
                        conflict.conflict_info?.day,
                        <EventIcon sx={{ fontSize: 16 }} />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderDetail(
                        "Assigned From",
                        conflict.conflict_info?.assigned_from,
                        <AccessTimeIcon sx={{ fontSize: 16 }} />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderDetail(
                        "Assigned To",
                        conflict.conflict_info?.assigned_to,
                        <AccessTimeIcon sx={{ fontSize: 16 }} />
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              bgcolor: "#F8FAFC",
              borderRadius: "8px",
              border: "1px dashed #E2E8F0",
            }}
          >
            <Typography variant="body2" color="#94A3B8">
              No conflict data available
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2.5,
          pt: 0,
          gap: 1.5,
          borderTop: "1px solid",
          borderColor: "#E2E8F0",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "10px",
            borderColor: "#D0D5DD",
            color: "#344054",
            px: 3,
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Close
        </Button>
        {onAssignAnyway && (
          <Button
            onClick={onAssignAnyway}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              bgcolor: theme.palette.error.main,
              px: 4,
              "&:hover": {
                bgcolor: theme.palette.error.dark,
                boxShadow: "0px 4px 12px rgba(220, 38, 38, 0.3)",
              },
            }}
          >
            Assign Anyway
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConflictModal;